// src/writer/components/TemplateSelector.tsx
import React, { useState } from 'react';
import { ProjectState } from '../types/project';
import { BookTemplate, TemplateCategory } from '../types';
import { templateCategories, applyTemplateToProject } from '../templates/bookTemplates';
import { primaryButtonClass, secondaryButtonClass, actionButtonClass } from '../constants';

interface TemplateSelectorProps {
    isOpen: boolean;
    onClose: () => void;
    onApplyTemplate: (templateData: Partial<ProjectState>) => void;
    currentProjectState: ProjectState;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
    isOpen,
    onClose,
    onApplyTemplate,
    currentProjectState
}) => {
    const [selectedCategory, setSelectedCategory] = useState<string>('fiction');
    const [selectedTemplate, setSelectedTemplate] = useState<BookTemplate | null>(null);
    const [showConfirmation, setShowConfirmation] = useState(false);

    if (!isOpen) return null;

    const currentCategory = templateCategories.find(cat => cat.id === selectedCategory);

    const handleTemplateSelect = (template: BookTemplate) => {
        setSelectedTemplate(template);
        setShowConfirmation(true);
    };

    const handleApplyTemplate = () => {
        if (!selectedTemplate) return;
        
        const templateData = applyTemplateToProject(selectedTemplate);
        onApplyTemplate(templateData as Partial<ProjectState>);
        setShowConfirmation(false);
        setSelectedTemplate(null);
        onClose();
    };

    const handleCancel = () => {
        setShowConfirmation(false);
        setSelectedTemplate(null);
    };

    // Check if current project has significant content
    const hasExistingContent = () => {
        const { frontMatter, backMatter, authorDetails, bookMetadata } = currentProjectState;
        
        const frontMatterContent = Object.values(frontMatter || {}).some(value => 
            value && typeof value === 'string' && value.trim().length > 0
        );
        const backMatterContent = Object.values(backMatter || {}).some(value => 
            value && typeof value === 'string' && value.trim().length > 0
        );
        const authorContent = Object.values(authorDetails || {}).some(value => 
            value && typeof value === 'string' && value.trim().length > 0
        );
        const metadataContent = Object.values(bookMetadata || {}).some(value => 
            value && typeof value === 'string' && value.trim().length > 0
        );

        return frontMatterContent || backMatterContent || authorContent || metadataContent;
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
                {!showConfirmation ? (
                    <>
                        <div className="p-6 border-b bg-gray-50">
                            <div className="flex justify-between items-center">
                                <h2 className="text-2xl font-bold text-gray-800">Choose Book Template</h2>
                                <button
                                    onClick={onClose}
                                    className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                                >
                                    ×
                                </button>
                            </div>
                            <p className="text-gray-600 mt-2">
                                Select a template to pre-populate your book structure with professional defaults
                            </p>
                        </div>

                        <div className="flex h-[70vh]">
                            {/* Category Sidebar */}
                            <div className="w-1/4 bg-gray-50 border-r p-4">
                                <h3 className="font-semibold text-gray-800 mb-4">Categories</h3>
                                <div className="space-y-2">
                                    {templateCategories.map(category => (
                                        <button
                                            key={category.id}
                                            onClick={() => setSelectedCategory(category.id)}
                                            className={`w-full text-left p-3 rounded-lg transition-all ${
                                                selectedCategory === category.id
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-white text-gray-700 hover:bg-blue-50'
                                            }`}
                                        >
                                            <div className="font-medium">{category.name}</div>
                                            <div className="text-xs opacity-75 mt-1">{category.description}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Templates Grid */}
                            <div className="flex-1 p-6 overflow-y-auto">
                                {currentCategory && (
                                    <>
                                        <h3 className="text-xl font-semibold mb-4">{currentCategory.name} Templates</h3>
                                        <div className="grid md:grid-cols-2 gap-6">
                                            {currentCategory.templates.map(template => (
                                                <div
                                                    key={template.id}
                                                    className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer"
                                                    onClick={() => handleTemplateSelect(template)}
                                                >
                                                    <div className="flex items-start justify-between mb-3">
                                                        <h4 className="text-lg font-semibold text-gray-800">{template.name}</h4>
                                                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                                            {template.genre}
                                                        </span>
                                                    </div>
                                                    
                                                    <p className="text-gray-600 text-sm mb-4">{template.description}</p>
                                                    
                                                    <div className="space-y-3">
                                                        <div>
                                                            <h5 className="text-sm font-medium text-gray-700 mb-1">Target Word Count:</h5>
                                                            <p className="text-sm text-gray-600">{template.targetWordCount?.toLocaleString() || 'Varies'} words</p>
                                                        </div>
                                                        
                                                        <div>
                                                            <h5 className="text-sm font-medium text-gray-700 mb-1">Common Elements:</h5>
                                                            <div className="flex flex-wrap gap-1">
                                                                {template.commonElements.slice(0, 4).map((element, index) => (
                                                                    <span key={index} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                                                        {element}
                                                                    </span>
                                                                ))}
                                                                {template.commonElements.length > 4 && (
                                                                    <span className="text-xs text-gray-500">+{template.commonElements.length - 4} more</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        
                                                        <div>
                                                            <h5 className="text-sm font-medium text-gray-700 mb-1">Structure:</h5>
                                                            <p className="text-xs text-gray-600">{template.suggestedStructure}</p>
                                                        </div>
                                                    </div>
                                                    
                                                    <button className={`w-full mt-4 ${primaryButtonClass} hover:bg-blue-700`}>
                                                        Select Template
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                ) : (
                    /* Confirmation Dialog */
                    <div className="p-8">
                        <div className="text-center mb-6">
                            <h3 className="text-2xl font-bold text-gray-800 mb-2">Apply Template</h3>
                            <p className="text-gray-600">
                                You're about to apply the <strong>{selectedTemplate?.name}</strong> template
                            </p>
                        </div>

                        {hasExistingContent() && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                                <div className="flex items-start space-x-3">
                                    <span className="text-yellow-600 text-xl">⚠️</span>
                                    <div>
                                        <h4 className="font-medium text-yellow-800">Existing Content Detected</h4>
                                        <p className="text-sm text-yellow-700 mt-1">
                                            Applying this template will overwrite your current book structure data. 
                                            Your chapters and world-building data will remain unchanged.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {selectedTemplate && (
                            <div className="bg-gray-50 rounded-lg p-6 mb-6">
                                <h4 className="font-semibold text-gray-800 mb-3">Template Details</h4>
                                <div className="grid md:grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="font-medium text-gray-700">Genre:</span> {selectedTemplate.genre}
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-700">Type:</span> {selectedTemplate.fictionVsNonFiction}
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-700">Target Words:</span> {selectedTemplate.targetWordCount?.toLocaleString() || 'Varies'}
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-700">Structure:</span> {selectedTemplate.bookMetadataDefaults.structure || 'Standard'}
                                    </div>
                                </div>
                                
                                <div className="mt-4">
                                    <h5 className="font-medium text-gray-700 mb-2">Professional Tips:</h5>
                                    <ul className="text-sm text-gray-600 space-y-1">
                                        {selectedTemplate.professionalTips.slice(0, 3).map((tip, index) => (
                                            <li key={index} className="flex items-start space-x-2">
                                                <span className="text-blue-600 mt-1">•</span>
                                                <span>{tip}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-center space-x-4">
                            <button
                                onClick={handleCancel}
                                className={`px-6 py-3 ${secondaryButtonClass} hover:bg-gray-600`}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleApplyTemplate}
                                className={`px-6 py-3 ${actionButtonClass} hover:bg-green-700`}
                            >
                                Apply Template
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TemplateSelector;